import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import useSEO from "../Hooks/UseSeo";
import { Helmet } from "react-helmet";

const EscortedTours = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: "Escorted Tours | Premium & Affordable Guided Holidays Worldwide",
    description:
      "Explore our Escorted Tours — guided group travel experiences offering comfort, expert guidance, and perfectly planned itineraries across the globe.",
    url: "https://www.yourtravelwebsite.com/escorted-tours",
  });

  return (
    <>
      <Helmet>
        <link
          rel="canonical"
          href="https://www.yourtravelwebsite.com/escorted-tours"
        />
      </Helmet>

      <Header />

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-12 mt-32">
        {/* 🌟 Hero Section */}
        <section className="relative bg-gray-100 rounded-lg overflow-hidden">
          <img
            src="https://img.freepik.com/free-photo/beautiful-girl-standing-boat-looking-mountains-ratchaprapha-dam-khao-sok-national-park-surat-thani-province-thailand_335224-849.jpg?semt=ais_hybrid&w=740&q=80"
            alt="Escorted Tours Hero"
            className="w-full h-80 object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white text-center">
              Escorted Tours Around the World
            </h1>
          </div>
        </section>

        {/* 🌟 Introduction */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">What are Escorted Tours?</h2>
          <p>
            One of the most popular and convenient ways to travel internationally
            is through <b>Escorted Tours</b>. These tours are designed for
            travelers who prefer a stress-free journey without the hassle of
            planning flights, accommodations, and sightseeing arrangements. With
            the help of an expert guide, you can discover destinations in greater
            depth, enjoy insider knowledge, and explore with complete peace of
            mind.
          </p>
          <p>
            In simple terms, escorted tours are <b>guided group journeys</b> that
            follow a pre-planned itinerary. They ensure that every part of your
            vacation — from transport to meals — is handled professionally,
            letting you focus solely on enjoying your travel experience.
          </p>
        </section>

        {/* 🌟 Key Advantages */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Key Advantages of Escorted Tours</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <b>Well-Planned Itinerary:</b> Everything from your flight bookings
              to local activities is carefully arranged.
            </li>
            <li>
              <b>Perfect for Large Destinations:</b> Visit the best attractions
              efficiently.
            </li>
            <li>
              <b>Expert Tour Guides:</b> Gain deeper insights into culture,
              history, and geography.
            </li>
            <li>
              <b>Hassle-Free Experience:</b> Every aspect is managed professionally.
            </li>
          </ul>
        </section>

        {/* 🌟 Discover Tours */}
        <section className="space-y-8">
          <h2 className="text-2xl font-semibold text-center">
            Discover Our Best Escorted Tours
          </h2>
          <p className="text-center max-w-3xl mx-auto">
            Our escorted tours are carefully crafted to offer immersive
            experiences — from Europe’s historical landmarks to Asia’s vibrant
            culture. Choose between <b>Premium Holidays</b> and{" "}
            <b>Affordable Touring</b>.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Premium */}
            <div className="border rounded-lg shadow hover:shadow-lg transition p-4 text-center">
              <img
                src="https://media.istockphoto.com/id/1297349747/photo/hot-air-balloons-flying-over-the-botan-canyon-in-turkey.jpg?s=612x612&w=0&k=20&c=kt8-RRzCDunpxgKFMBBjZ6jSteetNhhSxHZFvHQ0hNU="
                alt="Premium Holidays"
                className="w-full h-56 object-cover rounded"
              />
              <h3 className="text-xl font-semibold mt-3">Premium Holidays</h3>
              <p className="text-sm mt-2">
                Explore Europe, USA, Canada, Africa, Japan, and more in luxury.
              </p>
              <button
                className="mt-4 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                onClick={() => navigate("/contact-us")}
              >
                Click for Itinerary
              </button>
            </div>

            {/* Affordable */}
            <div className="border rounded-lg shadow hover:shadow-lg transition p-4 text-center">
              <img
                src="https://images.unsplash.com/photo-1756142754765-663403d56be0?fm=jpg&q=60&w=3000"
                alt="Affordable Touring"
                className="w-full h-56 object-cover rounded"
              />
              <h3 className="text-xl font-semibold mt-3">Affordable Touring</h3>
              <p className="text-sm mt-2">
                Affordable and expertly managed tours across Asia, Europe,
                Australia, and the Americas.
              </p>
              <button
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => navigate("/contact-us")}
              >
                Click for Itinerary
              </button>
            </div>
          </div>
        </section>

        {/* 🌟 PRICING SECTION */}
        <section className="py-12">
          <h2 className="text-3xl font-semibold text-center">
            Our Escorted Tour Pricing Plans
          </h2>
          <p className="text-center max-w-2xl mx-auto mt-2 text-gray-600">
            Choose the perfect travel experience — Premium luxury or
            Affordable adventure.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
            {/* Premium Plan */}
            <div className="border rounded-2xl shadow-lg hover:shadow-2xl transition p-6 bg-white">
              <h3 className="text-2xl font-bold text-yellow-600 text-center">
                Premium Escorted Holidays
              </h3>
              <p className="text-center text-gray-600 mt-2">
                Luxury hotels • Expert Guides • All-inclusive comfort
              </p>

              <p className="text-center text-4xl font-bold mt-5">
                ₹1,49,999{" "}
                <span className="text-lg text-gray-500">/ person</span>
              </p>

              <ul className="mt-6 space-y-3 text-gray-700">
                <li>✔ 4★ & 5★ Hotels</li>
                <li>✔ All Meals Included</li>
                <li>✔ Private AC Luxury Coach</li>
                <li>✔ Major Attraction Entry Fees</li>
                <li>✔ In-depth Guided Sightseeing</li>
                <li>✔ Travel Insurance Included</li>
              </ul>

              <button
                className="w-full mt-6 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 rounded-lg"
                onClick={() => navigate("/contact-us")}
              >
                Explore Premium Tours
              </button>
            </div>

            {/* Affordable Plan */}
            <div className="border rounded-2xl shadow-lg hover:shadow-2xl transition p-6 bg-white">
              <h3 className="text-2xl font-bold text-blue-600 text-center">
                Affordable Guided Tours
              </h3>
              <p className="text-center text-gray-600 mt-2">
                Value for money • Comfortable stays • Expert-managed trips
              </p>

              <p className="text-center text-4xl font-bold mt-5">
                ₹79,999{" "}
                <span className="text-lg text-gray-500">/ person</span>
              </p>

              <ul className="mt-6 space-y-3 text-gray-700">
                <li>✔ 3★ & 4★ Hotels</li>
                <li>✔ Breakfast + Select Meals</li>
                <li>✔ AC Coach Travel</li>
                <li>✔ Group Sightseeing</li>
                <li>✔ Well-planned Itinerary</li>
                <li>✔ 24×7 Tour Manager Support</li>
              </ul>

              <button
                className="w-full mt-6 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 rounded-lg"
                onClick={() => navigate("/contact-us")}
              >
                Explore Affordable Tours
              </button>
            </div>
          </div>
        </section>

        {/* 🌟 WHY CHOOSE US */}
        <section className="py-10">
          <h2 className="text-3xl font-semibold text-center">
            Why Choose Our Escorted Tours?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
            <div className="p-6 border rounded-lg shadow hover:shadow-lg transition">
              <h3 className="font-semibold text-xl mb-2">✔ Stress-Free Travel</h3>
              <p className="text-gray-600">
                We take care of everything — hotels, transport, meals, and sightseeing.
              </p>
            </div>

            <div className="p-6 border rounded-lg shadow hover:shadow-lg transition">
              <h3 className="font-semibold text-xl mb-2">✔ Expert Tour Guides</h3>
              <p className="text-gray-600">
                Explore destinations with professional multilingual guides.
              </p>
            </div>

            <div className="p-6 border rounded-lg shadow hover:shadow-lg transition">
              <h3 className="font-semibold text-xl mb-2">✔ Best Price Guarantee</h3>
              <p className="text-gray-600">
                Enjoy unmatched pricing and exceptional value in every trip.
              </p>
            </div>
          </div>
        </section>

        {/* 🌟 FAQ Section */}
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Frequently Asked Questions</h2>

          <div className="space-y-3">
            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                What is an escorted group tour?
              </summary>
              <p className="mt-2">
                An escorted tour is a professionally guided travel experience
                where an expert accompanies the group throughout the itinerary.
              </p>
            </details>

            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                What are the benefits of a guided tour?
              </summary>
              <p className="mt-2">
                Convenience, cultural insights, safety, and hassle-free travel
                planning.
              </p>
            </details>

            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                How do escorted tours differ from guided tours?
              </summary>
              <p className="mt-2">
                Escorted tours include complete logistics — hotels, meals,
                transportation, and sightseeing.
              </p>
            </details>

            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                Which is the best international escorted tour?
              </summary>
              <p className="mt-2">
                Depends on your interests — Europe, Asia, Africa, USA, and more.
              </p>
            </details>

            <details className="border rounded-lg p-3">
              <summary className="font-medium cursor-pointer">
                What are the most affordable European countries to visit?
              </summary>
              <p className="mt-2">
                Poland, Hungary, Romania, Czech Republic, and Croatia are great
                budget-friendly options.
              </p>
            </details>
          </div>
        </section>

        {/* 🌟 CTA Section */}
        <section className="text-center py-6">
          <p className="text-lg">
            Ready to experience the world with comfort and confidence? Contact
            our travel experts today at{" "}
            <a
              href="tel:+918447433852"
              className="text-yellow-600 font-semibold"
            >
              +91-8447433852
            </a>
          </p>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default EscortedTours;
